import { useEffect, useRef, useState } from "react";
import type { ReportPlace } from "../api";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ApiError,
  NOTHING_READ_YET,
  SIGN_IN_TO_CHAT,
  QUESTIONS_PAUSED,
  signInURL,
  askAboutChapter,
  bookKey,
  getBook,
  type AskAction,
  getNamedCharacters,
  recapCharacter,
  type Answer,
  type AskMode,
  type CharacterRecap,
  type Citation,
  type ReaderCharacter,
} from "../api";
import { answerRuns, meaningEntries, partRuns, type MeaningEntry, type Run } from "./answerText";
import CharacterFace from "../components/CharacterFace";
import ReportDialog from "../components/ReportDialog";
import SheetHeader from "../components/SheetHeader";
import { FlagIcon, ForwardIcon, SendIcon } from "../components/icons";
import { useT } from "../i18n";
import { useSession } from "../session";
import { chatHeight } from "./chatHeight";
import { useContainTouch } from "./containTouch";
import SpoilerNote, { showsSpoilerNote } from "./SpoilerNote";
import { historyFor, recentFor, threadKey, type ThreadStore } from "./conversation";

/**
 * ChatSheet is the reader's conversation with the book about one line.
 *
 * It began as a one-shot ask about the line under a long press. Readers do not
 * ask one question: they ask who someone is, then what happened to them, and
 * the second question means nothing without the first. So it is a chat, and
 * each follow-up carries the conversation so far (ADR-0022).
 *
 * One conversation per line. Long-pressing another line opens that line's,
 * empty the first time; the chat mark on a line reopens its own. The line is
 * shown above the input as what this conversation is about, and cannot be
 * detached — it is which conversation this is.
 *
 * The studio's long press opens the change sheet instead — edit, rewrite, or
 * fix a pronunciation — which changes the book for everyone who listens to it
 * (ADR-0025). Same gesture, different powers, decided by which route you are in.
 *
 * Asking fires on an explicit send only — never on a keystroke, never on a
 * debounce. One question, one charge.
 *
 * It opens compact, so the book stays readable above an empty conversation,
 * and takes its full height once there is something in it to read.
 */
export default function ChatSheet({
  chapterID,
  chapterNumber = 0,
  bookID,
  threads,
  line,
  offerCast,
  word,
  onVisit,
  onClose,
}: {
  chapterID: string;
  /**
   * The chapter asked from, as the reader's list numbers it, for what a
   * lineless question is about; 0 when it is not known.
   */
  chapterNumber?: number;
  /** The book, for its cast: a line naming a character offers the card. */
  bookID?: string;
  threads: ThreadStore;
  /**
   * The line the reader saw banded, or "" for a question about the story up
   * to their place (ADR-0176).
   */
  line: string;
  /**
   * Whether the reader chose the line. The chat button opens about the line
   * being read, and leading that with "who is …?" puts a question in the
   * reader's mouth they did not ask.
   */
  offerCast: boolean;
  /**
   * The word a long press landed on, whose Meaning is asked as the sheet
   * opens (ADR-0141, 11-01). None — a tap, the Ask button, a press between
   * words — asks nothing.
   */
  word?: string;
  /** Take the reader to the passage a citation came from. */
  onVisit: (citation: Citation) => void;
  onClose: () => void;
}) {
  // Which conversation this is: the line's, or with none the chapter's.
  const key = threadKey(line, chapterID);
  const at = (l: string) => threadKey(l, chapterID);
  const turns = threads.turnsFor(key);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const log = useRef<HTMLDivElement>(null);
  const t = useT();
  // Whether the book tells a story, as its author set it (ADR-0158): the empty
  // conversation's hint does not offer "what happened" on a book of letters.
  // The page has usually read the book already; until it has, a story.
  const book = useQuery({
    queryKey: bookKey(bookID ?? ""),
    queryFn: () => getBook(bookID!),
    enabled: !!bookID,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const story = book.data?.story ?? true;
  // A book that tells no story is asked openly, and the server decides that
  // (ADR-0160); the sheet sends and shows open only so it says what the
  // answer will be.
  const modeFor = (k: string): AskMode => (story ? threads.modeFor(k) : "open");

  const ask = useMutation({
    mutationFn: (v: {
      question: string;
      line: string;
      mode?: AskMode;
      action?: AskAction;
      phrase?: string;
    }) =>
      askAboutChapter(chapterID, {
        question: v.question,
        sentence: v.line || undefined,
        action: v.action,
        phrase: v.phrase,
        history: historyFor(threads.turnsFor(at(v.line))),
        // What was said about other lines, in this question's mode (ADR-0156).
        recent: recentFor(threads.threads, at(v.line), v.mode ?? modeFor(at(v.line))),
        // The conversation's mode, not the sheet's: the reader chose it for
        // this line, and it survives the sheet being closed (ADR-0071).
        mode: v.mode ?? modeFor(at(v.line)),
      }),
    // Filed under the line it was asked about, not whichever line is open
    // when the answer arrives: the sheet can be closed and another line
    // pressed while it is on its way.
    onSuccess: (answer, v) =>
      threads.add(at(v.line), {
        question: v.question,
        answer,
        chapter_id: chapterID,
        asked_at: Date.now(),
      }),
    onError: (err, v) => {
      // A visitor's refusal is an answer, not a failure (ADR-0070): it goes
      // into the thread under the question, with a way to sign in, and the
      // question does not come back to be retyped — signing in and asking
      // again is the path, and the question is already on screen.
      // The paused ceiling is the same kind of turn (ADR-0191).
      const code = err instanceof ApiError ? err.problem.code : undefined;
      if (code === SIGN_IN_TO_CHAT || code === QUESTIONS_PAUSED) {
        threads.add(at(v.line), {
          question: v.question,
          answer: {
            answer: "",
            citations: [],
            cost_micros: 0,
            remaining_this_hour: 0,
            grounded: false,
            reason: code,
          },
        });
        return;
      }
      setProblem(describe(err, t.chat.failed, t.chat.questionsPaused));
      // The question goes back where it was typed, so trying again is one tap
      // rather than typing it out a second time. A tapped one was never
      // typed, and its chip is still there.
      if (!v.action) setDraft((current) => current || v.question);
    },
  });

  const send = () => {
    const question = draft.trim();
    if (!question || ask.isPending) return;
    setProblem(null);
    setDraft("");
    ask.mutate({ question, line });
  };

  const mode = modeFor(key);

  /** One tap, one question (ADR-0141): the label is the reader's turn. */
  const tap = (question: string, action: AskAction, phrase?: string) => {
    if (ask.isPending) return;
    setProblem(null);
    ask.mutate({ question, line, action, phrase });
  };
  // Any line gets them, the Ask button's too (ADR-0141, amended by the owner).
  const offerActions = line.trim() !== "";

  // A long press on a word is the question (ADR-0141, 11-01): asked once as
  // the sheet opens, for a signed-in reader, unless this line's conversation
  // already holds it — then the answer it has is what they see. A visitor
  // gets the sheet as before and nothing is sent: a refusal turn they did
  // not ask for would read as the press having failed.
  const { isSignedIn, isPending: sessionPending } = useSession();
  const pressedAsked = useRef<string | null>(null);
  useEffect(() => {
    if (!word || !offerActions || sessionPending || !isSignedIn) return;
    const question = t.chat.meaningOf(`\u2068${word}\u2069`);
    const once = `${key}\u0000${question}`;
    // Refs outlive StrictMode's second run of this effect, so it asks once.
    if (pressedAsked.current === once) return;
    pressedAsked.current = once;
    if (threads.turnsFor(key).some((turn) => turn.question === question)) return;
    tap(question, "meaning", word);
    // Only a new press asks; the sheet re-rendering as answers land does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word, key, offerActions, sessionPending, isSignedIn]);

  /**
   * Asking the same question openly, from the answer that had nothing to
   * answer from.
   *
   * It switches the conversation as well as re-sending, because a reader who
   * takes this offer has decided how they want the rest of this conversation
   * answered — and the mode is passed explicitly rather than read back, since
   * the state update has not landed by the time the request is built.
   */
  // The spoiler note's way out (ADR-0188): offered only while the thread is
  // open, since a switch to the mode it is already in would do nothing.
  const spoilerFree = mode === "open" ? () => threads.setMode(key, "spoiler_free") : undefined;
  const askOpenly = (question: string) => {
    if (ask.isPending) return;
    setProblem(null);
    threads.setMode(key, "open");
    ask.mutate({ question, line, mode: "open" });
  };

  // Newest at the bottom, like every chat: follow it as it grows, and open
  // there rather than at the start of a long conversation.
  useEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, ask.isPending]);

  const last = turns.at(-1)?.answer;
  const pendingHere = ask.isPending && ask.variables.line === line;

  /**
   * "Who is this?" costs nothing: the published cast already says. A line
   * that names a character offers the card first — who they were when the
   * story introduced them, never their whole arc (requirement 0011). The
   * server finds the names, folded and word by word, as it finds everything
   * else in Persian text.
   */
  const cast = useQuery({
    queryKey: ["characters", "named", chapterID, line],
    queryFn: () => getNamedCharacters(chapterID, line),
    enabled: offerCast && !!bookID && line.trim() !== "",
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const named = offerCast ? (cast.data?.characters ?? []) : [];
  const [showing, setShowing] = useState<ReaderCharacter | null>(null);

  /**
   * "What do I know about them so far?" is a question about one character, and
   * like any question it spends — so only on this tap (ADR-0013). Answers are
   * kept per line and character for as long as the sheet is open.
   */
  const [recaps, setRecaps] = useState<Record<string, CharacterRecap>>({});
  const recap = useMutation({
    mutationFn: (v: { character: ReaderCharacter; line: string }) =>
      recapCharacter(chapterID, v.character.id, v.line),
    onSuccess: (answer, v) =>
      setRecaps((was) => ({ ...was, [`${v.character.id}:${v.line}`]: answer })),
  });
  const shownRecap = showing ? recaps[`${showing.id}:${line}`] : undefined;
  const recapping =
    recap.isPending &&
    recap.variables.character.id === showing?.id &&
    recap.variables.line === line;

  const [grown, setGrown] = useState(false);
  const height = chatHeight({
    turns: turns.length,
    pending: pendingHere,
    showingCard: !!showing,
    grown,
  });
  useEffect(() => {
    if (height === "full") setGrown(true);
  }, [height]);
  // Whether the "who is" row may fill in. Its room is kept from the start, so
  // the lookup landing while the reader types does not move the input.
  const castRow = offerCast && !!bookID && line.trim() !== "";

  const sheet = useRef<HTMLDivElement>(null);
  useContainTouch(sheet);

  // Reporting a problem (requirement 0122): the quoted line, or a face on the
  // card. Signed-in readers only, and nothing new on the reading page.
  const canReport = isSignedIn && !!bookID;
  const [reporting, setReporting] = useState<{ place: ReportPlace; about: string } | null>(null);

  return (
    <div
      ref={sheet}
      role="dialog"
      aria-label={t.chat.title}
      data-testid="ask-sheet"
      data-height={height}
      /* Above the transport (z-20) and the reader's menu button (z-30), or the
         controls paint over the sheet — which is exactly what happened to the
         correction sheet before it was given a stacking order of its own.
         Full is a fixed height rather than a maximum: a conversation that
         grew the sheet with every answer would move the input under the
         thumb. It grows once, from compact, as a question is sent — when the
         thumb has just left the input. */
      className={`fixed inset-x-0 bottom-0 z-40 flex flex-col overscroll-contain rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)] ${
        height === "full" ? "h-[85dvh]" : "max-h-[85dvh]"
      }`}
    >
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col">
        <SheetHeader title={t.chat.title} onClose={onClose} closeTestID="ask-close">
          {turns.length > 0 && (
            <button
              type="button"
              data-testid="chat-new"
              onClick={() => {
                threads.clear(key);
                setProblem(null);
              }}
              disabled={ask.isPending}
              className="rounded-control px-2.5 py-1.5 text-xs text-ink-soft transition-colors hover:bg-sunk disabled:opacity-40"
            >
              {t.chat.newChat}
            </button>
          )}
        </SheetHeader>

        <div
          ref={log}
          data-testid="chat-log"
          className={`flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 ${
            height === "full" ? "py-4" : "py-2.5"
          }`}
        >
          {(castRow || named.length > 0) && (
            /* One line that scrolls sideways, so a long cast cannot wrap and
               push the input down either. */
            <div
              className="-mx-4 flex min-h-[1.625rem] gap-2 overflow-x-auto overscroll-contain px-4"
              data-testid="who-is"
            >
              {named.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid="who-is-chip"
                  aria-pressed={showing?.id === c.id}
                  onClick={() => setShowing((was) => (was?.id === c.id ? null : c))}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border py-1 pe-3 text-xs whitespace-nowrap transition-colors ${
                    c.portrait ? "ps-1" : "ps-3"
                  } ${
                    showing?.id === c.id
                      ? "border-sage bg-sage/15 text-ink"
                      : "border-line text-ink-soft hover:bg-sunk"
                  }`}
                  dir="auto"
                >
                  {/* The face, when the edition carries one (ADR-0077). Only
                      when it does: an initial in a chip this small reads as a
                      bullet, so here the chip simply keeps its own padding. */}
                  {c.portrait && <CharacterFace character={c} size={20} />}
                  {/* Isolated, or a Persian name in an English sentence drags
                      the question mark to its own end. */}
                  {t.chat.whoIs(`\u2068${c.name}\u2069`)}
                </button>
              ))}
            </div>
          )}
          {showing && (
            <div
              data-testid="who-is-card"
              className="rounded-card border border-line bg-sunk px-4 py-3"
            >
              {/* dir="auto" on each block of authored text, not on the card:
                  the button below is in the interface's language, and inside
                  an auto-directed Persian card its question mark would swap
                  ends. */}
              <div className="flex items-start gap-3">
                <CharacterFace character={showing} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium" dir="auto">
                    {showing.name}
                    {showing.role && (
                      <span className="font-normal text-muted"> · {showing.role}</span>
                    )}
                  </p>
                  {showing.introduction && (
                    <p className="mt-1 text-sm leading-6 text-ink-soft" dir="auto">
                      {showing.introduction}
                    </p>
                  )}
                </div>
                {canReport && (
                  <button
                    type="button"
                    aria-label={t.report.portrait}
                    title={t.report.portrait}
                    data-testid="character-report"
                    onClick={() =>
                      setReporting({
                        place: {
                          place: "character",
                          character_id: showing.id,
                          chapter_id: chapterID,
                        },
                        about: showing.name,
                      })
                    }
                    className="-me-2 -mt-1 grid size-8 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-surface hover:text-ink"
                  >
                    <FlagIcon className="size-3.5" />
                  </button>
                )}
              </div>
              {shownRecap ? (
                <div className="mt-3 border-t border-line pt-3" data-testid="so-far">
                  <p className="text-sm leading-6" dir="auto" data-testid="so-far-text">
                    {shownRecap.text}
                  </p>
                </div>
              ) : (
                <div className="mt-3">
                  <button
                    type="button"
                    data-testid="so-far-ask"
                    disabled={recapping}
                    onClick={() => recap.mutate({ character: showing, line })}
                    className="rounded-control border border-line bg-surface px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
                  >
                    {recapping ? t.chat.soFarReading : t.chat.soFar}
                  </button>
                  <p className="mt-1 text-xs text-muted">{t.chat.soFarNote}</p>
                  {recap.isError && !recapping && recap.variables.character.id === showing.id && (
                    <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {describe(recap.error, t.chat.soFarFailed, t.chat.questionsPaused)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {turns.length === 0 && !pendingHere && !showing && (
            <p className="text-sm text-muted" data-testid="chat-empty">
              {story ? t.chat.empty : t.chat.emptyNotStory}
            </p>
          )}
          {turns.map((turn, i) => (
            <Exchange
              key={i}
              question={turn.question}
              answer={turn.answer}
              // Said once, where page-only answers begin, rather than under
              // every one of them: a book's mode does not change between two
              // questions, so the note is marking the run, and it comes back
              // if an answer between them was searched (ADR-0014, ADR-0022).
              markPageOnly={!turn.answer.grounded && (turns[i - 1]?.answer.grounded ?? true)}
              onVisit={onVisit}
              onAskOpenly={askOpenly}
              story={story}
              onSpoilerFree={spoilerFree}
            />
          ))}
          {pendingHere && (
            <Exchange question={ask.variables.question} markPageOnly={false} onVisit={onVisit} />
          )}
        </div>

        <div className="border-t border-line px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {line && (
            <div
              className="mb-2 rounded-control border-s-2 border-sage bg-sunk px-2.5 py-1.5"
              data-testid="ask-context-chip"
            >
              {/* dir="auto" on this line and nothing inside it: an element with
                  dir="auto" skips descendants carrying their own dir. */}
              <p
                dir="auto"
                data-testid="ask-context"
                className="line-clamp-2 text-xs leading-5 text-muted"
              >
                {line}
              </p>
            </div>
          )}
          {line && canReport && (
            /* Quiet: a link under the line, not a chip beside the questions. */
            <button
              type="button"
              data-testid="ask-report"
              onClick={() =>
                setReporting({
                  place: { place: "sentence", chapter_id: chapterID, sentence: line },
                  about: line,
                })
              }
              className="-mt-1 mb-2 text-xs text-muted underline-offset-2 hover:text-ink hover:underline"
            >
              {t.report.action}
            </button>
          )}
          {!line && chapterNumber > 0 && (
            <div
              className="mb-2 rounded-control border-s-2 border-sage bg-sunk px-2.5 py-1.5"
              data-testid="ask-place-chip"
            >
              <p className="text-xs leading-5 text-muted">{t.chat.aboutPlace(chapterNumber)}</p>
            </div>
          )}

          {problem && (
            <p
              role="alert"
              data-testid="ask-error"
              className="mb-2 text-sm text-red-600 dark:text-red-400"
            >
              {problem}
            </p>
          )}

          {/* Only when it is about to matter. A countdown on every answer turns
              a conversation into a meter. */}
          {last && last.remaining_this_hour <= 5 && (
            <p className="mb-2 text-xs text-muted" data-testid="ask-remaining">
              {t.chat.remaining(last.remaining_this_hour)}
            </p>
          )}

          {/* The one-tap questions about the line (ADR-0141). Tools, not
              starters: they stay after answers, and each tap is one turn. */}
          {offerActions && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="ask-actions">
              {[
                {
                  key: "meaning",
                  // Always the line: a word is asked by pressing it, and a
                  // chip naming it was unreadable on a phone (11-01).
                  label: t.chat.meaning,
                  ask: () => tap(t.chat.hardWords, "meaning"),
                },
                {
                  key: "explain",
                  label: t.chat.explain,
                  ask: () => tap(t.chat.explainThis, "explain"),
                },
                {
                  key: "simpler",
                  label: t.chat.simpler,
                  ask: () => tap(t.chat.sayItSimpler, "simpler"),
                },
              ].map((a) => (
                <button
                  key={a.key}
                  type="button"
                  data-testid={`ask-action-${a.key}`}
                  disabled={ask.isPending}
                  onClick={a.ask}
                  className="rounded-full border border-sage/45 bg-sage/15 px-3 py-1 text-sm font-medium text-ink transition-colors hover:bg-sage/25 disabled:opacity-50"
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}

          {/* What the next question may be answered from (ADR-0071), on a
              book that tells a story; any other is always open (ADR-0160). Chips
              rather than a menu: two options, both visible, one tap — and a
              line under them saying what the chosen one means, because "Open"
              on its own does not tell a reader it may give the book away. */}
          {story && (
            <div className="mb-2" data-testid="ask-mode">
              <div
                role="group"
                aria-label={t.chat.mode}
                className="flex gap-1 rounded-control bg-sunk p-1"
              >
                {(["spoiler_free", "open"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={mode === option}
                    data-testid={`ask-mode-${option}`}
                    onClick={() => threads.setMode(key, option)}
                    className={`flex-1 rounded-[0.55rem] px-3 py-1.5 text-xs transition-colors ${
                      mode === option
                        ? "bg-surface font-medium text-ink"
                        : "text-muted hover:text-ink"
                    }`}
                  >
                    {option === "open" ? t.chat.modeOpen : t.chat.modeSpoilerFree}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[0.68rem] leading-4 text-muted">
                {mode === "open" ? t.chat.modeOpenNote : t.chat.modeSpoilerFreeNote}
              </p>
            </div>
          )}

          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <label className="min-w-0 flex-1">
              <span className="sr-only">{t.chat.yourQuestion}</span>
              <textarea
                dir="auto"
                /* Not when there is a line: the one-tap questions come first,
                   and a phone's keyboard would cover the page (ADR-0141). */
                autoFocus={!offerActions}
                rows={1}
                value={draft}
                maxLength={500}
                enterKeyHint="send"
                data-testid="ask-input"
                placeholder={turns.length ? t.chat.placeholderFollowUp : t.chat.placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends and Shift+Enter breaks the line. Not while an
                  // input method is composing, where Enter confirms a word.
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
                /* text-base, not text-sm: iOS zooms the page into any input
                   under 16px when it takes focus. */
                className="block max-h-32 w-full resize-none rounded-control border border-line bg-surface px-3 py-2 text-base [field-sizing:content]"
              />
            </label>
            <button
              type="submit"
              aria-label={t.chat.send}
              data-testid="ask-submit"
              disabled={draft.trim() === "" || ask.isPending}
              className="grid size-10 shrink-0 place-items-center rounded-full bg-sage text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              <SendIcon />
            </button>
          </form>
        </div>
      </div>
      {reporting && bookID && (
        <ReportDialog
          bookID={bookID}
          edition={book.data?.edition?.number}
          place={reporting.place}
          about={reporting.about}
          onClose={() => setReporting(null)}
        />
      )}
    </div>
  );
}

/**
 * Exchange is one question and its answer — or, while it is being asked, the
 * question and a sign that the book is being read.
 */
function Exchange({
  question,
  answer,
  markPageOnly,
  onVisit,
  onAskOpenly,
  story = false,
  onSpoilerFree,
}: {
  question: string;
  answer?: Answer;
  markPageOnly: boolean;
  onVisit: (citation: Citation) => void;
  /** Ask this same question again, openly. Absent while one is in flight. */
  onAskOpenly?: (question: string) => void;
  /** Whether the book tells a story, so an open answer may spoil it. */
  story?: boolean;
  /** Switch the thread to spoiler-free; absent when it already is. */
  onSpoilerFree?: () => void;
}) {
  const t = useT();
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <p
          dir="auto"
          data-testid="chat-question"
          className="max-w-[85%] rounded-card rounded-ee-md bg-sage-soft px-3 py-2 text-[0.95rem] leading-7 whitespace-pre-wrap"
        >
          {question}
        </p>
      </div>

      {answer ? (
        <AnswerView
          answer={answer}
          markPageOnly={markPageOnly}
          onVisit={onVisit}
          onAskOpenly={onAskOpenly && (() => onAskOpenly(question))}
          story={story}
          onSpoilerFree={onSpoilerFree}
        />
      ) : (
        <p className="text-sm text-muted" data-testid="chat-pending" aria-live="polite">
          {t.chat.reading}
        </p>
      )}
    </div>
  );
}

function AnswerView({
  answer,
  markPageOnly,
  onVisit,
  onAskOpenly,
  story,
  onSpoilerFree,
}: {
  answer: Answer;
  markPageOnly: boolean;
  onVisit: (citation: Citation) => void;
  onAskOpenly?: () => void;
  story: boolean;
  onSpoilerFree?: () => void;
}) {
  const t = useT();

  /**
   * The answer nobody was charged for (ADR-0071): asked before anything had
   * been read, so there was no text to answer from and no call was made.
   *
   * The words are the client's, in the reader's own interface language, rather
   * than a sentence bought from a model in the language of the question — and
   * the way on is the same question asked openly, which does not depend on how
   * far they have read.
   */
  if (answer.reason === SIGN_IN_TO_CHAT) {
    return (
      <div data-testid="ask-answer">
        <p className="text-[0.95rem] leading-7">{t.chat.signInToChat}</p>
        <a
          href={signInURL()}
          data-testid="ask-sign-in"
          className="mt-2 inline-flex rounded-control bg-sage px-3.5 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep"
        >
          {t.nav.signIn}
        </a>
      </div>
    );
  }

  if (answer.reason === QUESTIONS_PAUSED) {
    return (
      <div data-testid="ask-answer">
        <p className="text-[0.95rem] leading-7" data-testid="ask-paused">
          {t.chat.questionsPaused}
        </p>
      </div>
    );
  }

  if (answer.reason === NOTHING_READ_YET) {
    return (
      <div data-testid="ask-answer">
        <p className="text-[0.95rem] leading-7">{t.chat.nothingRead}</p>
        {onAskOpenly && (
          <button
            type="button"
            onClick={onAskOpenly}
            data-testid="ask-openly"
            className="mt-2 rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
          >
            {t.chat.askOpen}
          </button>
        )}
      </div>
    );
  }

  const entries = meaningEntries(answer.answer);
  return (
    <div data-testid="ask-answer">
      {showsSpoilerNote(answer, story) && <SpoilerNote onSpoilerFree={onSpoilerFree} />}
      {entries ? (
        <MeaningCards entries={entries} />
      ) : (
        <p dir="auto" className="text-[0.95rem] leading-7 whitespace-pre-wrap">
          <Runs runs={answerRuns(answer.answer)} />
        </p>
      )}

      {/* An open answer may hold things the book has not said, so it says so —
          on the answer, never on the thread, because a reader may switch
          mid-conversation and the two must not render alike (ADR-0014). */}
      {answer.mode === "open" && (
        <p
          className="mt-2 flex items-start gap-1.5 text-xs text-muted"
          data-testid="ask-open-answer"
        >
          <span aria-hidden="true">✳</span>
          <span>{t.chat.openAnswer}</span>
        </p>
      )}

      {answer.citations.length > 0 && (
        <div className="mt-3">
          <h3 className="mb-1.5 text-xs font-medium text-muted">{t.chat.fromTheBook}</h3>
          <ul className="space-y-1.5">
            {answer.citations.map((c, i) => (
              <li key={`${c.chapter_id}-${c.offset}-${i}`}>
                {/* A citation you cannot visit is just a quotation. This is the
                    difference between being shown the evidence and being able
                    to go and read it. */}
                <button
                  type="button"
                  data-testid="ask-citation"
                  onClick={() => onVisit(c)}
                  className="w-full rounded-control border-s-2 border-sky bg-sunk px-3 py-2 text-start transition-colors hover:bg-sky-soft"
                >
                  <p dir="auto" className="text-xs leading-6 text-ink-soft">
                    {c.text}
                  </p>
                  {/* dir="auto" on the title alone: it is authored text,
                      and the chevron follows the interface, not the title. */}
                  <p className="mt-1 flex items-center gap-1 text-[0.68rem] text-muted">
                    <span dir="auto" className="min-w-0 truncate">
                      {c.chapter_title}
                    </span>
                    <ForwardIcon className="size-3 shrink-0" />
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* A grounded answer that cites nothing is the honest "the book does not
          say": somebody searched, and it is not there. Never under Meaning's
          cards: a word's sense is the language's, not the book's, so the note
          says nothing there (owner, 2026-10-31). Its prose fallback keeps it. */}
      {answer.grounded && answer.citations.length === 0 && !entries && (
        <p className="mt-2 text-xs text-muted" data-testid="ask-ungrounded">
          {t.chat.notInBook}
        </p>
      )}

      {/* A contextual answer is a different claim and must never be mistaken
          for a grounded one: nobody searched the book. Marked where a run of
          them starts, which is where a reader decides how much to trust what
          follows (ADR-0014). Never on an open answer: it may have read ahead,
          so "the book so far" would be false (ADR-0188). */}
      {markPageOnly && answer.mode !== "open" && (
        <p
          className="mt-2 flex items-start gap-1.5 text-xs text-muted"
          data-testid="ask-contextual"
        >
          <span aria-hidden="true">◌</span>
          <span>{t.chat.pageOnly}</span>
        </p>
      )}
    </div>
  );
}

function describe(err: unknown, fallback: string, paused: string): string {
  if (!(err instanceof ApiError)) return fallback;
  // The server's detail is English; the paused line is the reader's language.
  if (err.problem.code === QUESTIONS_PAUSED) return paused;
  return err.problem.detail ?? err.problem.title;
}

function Runs({ runs }: { runs: Run[] }) {
  return runs.map((r, i) =>
    r.bold ? (
      <strong key={i} className="font-semibold">
        {r.text}
      </strong>
    ) : r.italic ? (
      <em key={i}>{r.text}</em>
    ) : (
      r.text
    ),
  );
}

/**
 * A Meaning answer as one card per word (ADR-0216). Each part is its own
 * dir="auto" block, siblings and never nested, and the card carries no
 * direction: the heading is the book's language, the senses may be the
 * interface's.
 */
function MeaningCards({ entries }: { entries: MeaningEntry[] }) {
  return (
    <div data-testid="meaning-entries" className="space-y-2.5">
      {entries.map((e, i) => (
        <div
          key={i}
          data-testid="meaning-entry"
          className="rounded-card border border-line bg-surface px-3.5 py-3"
        >
          <p
            dir="auto"
            data-testid="meaning-heading"
            className="font-brand text-[1.02rem] font-bold text-ink"
          >
            {e.heading}
          </p>
          {e.plain && (
            <p
              dir="auto"
              data-testid="meaning-plain"
              className="mt-1 text-[0.9rem] leading-6 text-muted"
            >
              <Runs runs={partRuns(e.plain)} />
            </p>
          )}
          <p
            dir="auto"
            data-testid="meaning-here"
            className="mt-2 border-t border-line pt-2 text-[0.95rem] leading-7"
          >
            <Runs runs={partRuns(e.here)} />
          </p>
        </div>
      ))}
    </div>
  );
}
